import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Formik, Field, Form } from 'formik';
import { Box, Button, Text } from '@components/common';
import SvgIcon from '@components/SvgIcon';
import { InputField, PhoneField } from '@components/form';
import API from '@utils/api';
import history from '@utils/history';
import { getErrors } from '@utils/error';

import validationSchema from './schema';
import SignupActions, {
  SIGNUP_STAGE,
  SignupSelectors
} from '@redux/SignupRedux';
import AuthActions from '@redux/AuthRedux';
import AppActions, { LOGIN_PAGES } from '@redux/AppRedux';

const LockIcon = styled(SvgIcon)`
  position: absolute;
  right: 12px;
  top: 20px;
`;

class ContactForm extends React.Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    initialFormValues: PropTypes.object,
    loginInfo: PropTypes.object,
    extraInfo: PropTypes.object,
    ssoToken: PropTypes.any
  };

  handleSubmit = async (values, actions) => {
    const { loginInfo, extraInfo, onSubmit, ssoToken } = this.props;
    const { setSubmitting, setErrors } = actions;
    setSubmitting(true);

    try {
      if (!ssoToken) {
        const { result } = await API.reigsterWithEmail({
          ...loginInfo,
          ...extraInfo,
          ...values
        });
        setSubmitting(false);
        const { authToken, refreshToken, user } = result;
        onSubmit({ authToken, refreshToken }, user);
      } else {
        const { firstName, lastName, phoneNumber } = values;
        const { email } = loginInfo;
        await API.updateBasicProfile(
          {
            email,
            firstName,
            lastName,
            phone: phoneNumber,
            hasPets: false,
            optInNewsLetter: false
          },
          ssoToken.authToken
        );
        onSubmit(ssoToken);
      }
    } catch (err) {
      const error = getErrors(err);
      const errors = {
        phoneNumber: error.message || 'Submitted data is not valid'
      };

      setErrors(errors);
    }
    setSubmitting(false);
  };

  renderLockIcon = () => {
    return <LockIcon name="lock" width={16} height={16} />;
  };

  render() {
    const { initialFormValues } = this.props;
    const { onCancel, ssoToken } = this.props;

    return (
      <Formik
        initialValues={initialFormValues}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
        initialErrors={validationSchema.isValidSync(initialFormValues)}
      >
        {({ isValid, isSubmitting }) => {
          const isLocked = !!ssoToken && !ssoToken.ssoType;

          return (
            <Form>
              <Box display="flex" flexDirection="column">
                <Text fontSize={1} mb={4} color="lightText">
                  Tell us about you
                </Text>
                <Field
                  component={InputField}
                  mb={4}
                  name="firstName"
                  type="text"
                  label="First Name"
                  readOnly={isLocked}
                  renderAddon={isLocked ? this.renderLockIcon : undefined}
                />
                <Field
                  component={InputField}
                  mb={4}
                  name="lastName"
                  type="text"
                  label="Last Name"
                  readOnly={isLocked}
                  renderAddon={isLocked ? this.renderLockIcon : undefined}
                />
                <Field
                  component={PhoneField}
                  mb={4}
                  name="phoneNumber"
                  type="text"
                  label="Phone Number"
                />
                <Box flex={1} mb={4} display="flex" flexDirection="row">
                  <Button
                    variant="secondary"
                    flex={1}
                    mr={4}
                    type="button"
                    onClick={() => {
                      if (ssoToken) {
                        history.goBack();
                      }
                      onCancel();
                    }}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                  <Button
                    flex={1}
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

const mapStatesToProps = state => ({
  loginInfo: SignupSelectors.selectLoginInfo(state),
  extraInfo: SignupSelectors.selectExtraInfo(state),
  initialFormValues: SignupSelectors.selectContactInfo(state),
  ssoToken: SignupSelectors.selectSSOToken(state)
});

const mapDispatchToProps = dispatch => ({
  onSubmit: (token, user) => {
    if (user) {
      dispatch(AuthActions.setUser(user));
    }
    dispatch(AuthActions.setLoggedIn(token));
    dispatch(AppActions.setAppDataLoadRequest(LOGIN_PAGES['SIGN_UP']));
  },
  onCancel: () => dispatch(SignupActions.setSignupStep(SIGNUP_STAGE.EMAIL))
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(ContactForm);
